// Provider chain — outermost first:
//   AuthProvider (react-oidc-context) wraps the OIDC user lifecycle.
//   QueryClientProvider hands TanStack Query its singleton client.
//   I18nextProvider gives every component access to the translator.
//   RouterProvider drives navigation.
//
// Order matters: TopBar uses useAuth + useTranslation; both must be in
// scope by the time the router renders.

import { I18nextProvider } from "react-i18next";
import { AuthProvider } from "react-oidc-context";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";

import { queryClient } from "@/api/queryClient";
import { DevUserSwitcher } from "@/auth/dev-user-switcher";
import { oidcConfig } from "@/auth/oidc-config";
import { useClearTabsOnSubjectChange } from "@/auth/session-tabs";
import { useAuthFetch } from "@/auth/useAuthFetch";
import { i18n } from "@/i18n";
import { useApplyResolvedLocale } from "@/i18n/useApplyResolvedLocale";
import { router } from "@/router";

// Inside the AuthProvider tree so useAuthFetch can read the OIDC user and
// keep the API client's Bearer injector in sync (B0 deliverable 5). Renders
// the router; holds no state of its own.
function AuthenticatedRouter(): React.ReactElement {
  useAuthFetch();
  // Çıkışsız bir kullanıcı değişimi öncekinin sekme şeridini devralmasın
  // (SEM-8116). Router'dan ÖNCE koşar: silme, TabStrip mount olmadan biter.
  useClearTabsOnSubjectChange();
  // useAuthFetch'ten SONRA: /v1/me çağrısı Bearer enjektörü bağlanmadan
  // koşarsa 401 alır ve dil claim'i hiç gelmez.
  useApplyResolvedLocale();
  return <RouterProvider router={router} />;
}

export function App(): React.ReactElement {
  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <AuthenticatedRouter />
          {/* DEV-ONLY fixed-corner user switcher; renders null in production. */}
          <DevUserSwitcher />
        </I18nextProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
